import classNames from 'classnames';
import {
    CSSProperties,
    HTMLProps,
    ReactNode,
    useEffect,
    useRef,
    useState,
} from 'react';

import filterEmptyRecursive from '../../helpers/array/filterEmptyRecursive';
import ErrorList from '../../types/UI/ErrorList';
import fieldStyles from '../BaseField.module.css';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import Label from '../Label/Label';
import SVG from '../SVG/SVG';
import Stack from '../Stack/Stack';

import icon from './graphics/arrow.svg?raw';
import isOptionGroup from './helpers/isOptionGroup';
import Option from './types/Option';
import OptionsGroup from './types/OptionGroup';
import Variant from './types/Variant';

import styles from './Select.module.css';

type SelectProps<T extends string> = Omit<
    HTMLProps<HTMLSelectElement>,
    | 'onChange'
    | 'label'
    | 'multiple'
    | 'children'
    | 'options'
    | 'width'
    | 'size'
> & {
    options: Array<Option<T>> | Array<OptionsGroup>;
    label?: ReactNode;
    variant?: Variant;
    errors?: ErrorList;
    size?: 'small' | 'medium' | 'large';
    children?: ReactNode;
    bold?: boolean;
    preventShrink?: boolean;
    onChange?: (value: T | '') => void;
    placeholder?: string;
    inline?: boolean;
    flex?: true | number;
};

const renderOptions = (options: Array<Option | OptionsGroup>) => {
    return options.map((option) => {
        if (isOptionGroup(option)) {
            return (
                <optgroup label={option.label} key={option.label}>
                    {renderOptions(option.options)}
                </optgroup>
            );
        }

        return (
            <option
                value={option.value}
                key={option.value}
                disabled={option.disabled}
            >
                {option.label}
            </option>
        );
    });
};

function Select<T extends string>(props: SelectProps<T>) {
    const {
        label,
        options,
        errors,
        variant = 'default',
        className,
        defaultValue,
        onChange,
        size = 'medium',
        readOnly,
        disabled,
        preventShrink,
        children,
        placeholder,
        bold,
        flex,
        inline,
        ...inputProps
    } = props;

    const filteredErrors = filterEmptyRecursive(errors);

    const selectRef = useRef<HTMLSelectElement>(null);
    const [localValue, setLocalValue] = useState(defaultValue || '');
    const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newValue = event.target.value as T;
        setLocalValue(newValue);

        if (props.onChange) {
            props.onChange(newValue);
        }
    };

    /**
     * Set the first option of the select as the value when no defaultValue
     * is given. This is required for the styling.
     */
    useEffect(() => {
        if (!selectRef.current) {
            return;
        }

        if (localValue || defaultValue) {
            return;
        }

        setLocalValue(selectRef.current.value);
    }, [options]);

    /**
     * Trigger the onChange event when the value no longer appears in the
     * options.
     */
    useEffect(() => {
        if (!localValue) {
            return;
        }

        const option = options.find((option) => {
            if (isOptionGroup(option)) {
                return option.options.some((opt) => opt.value === localValue);
            }

            return option.value === localValue;
        });

        if (!option) {
            onChange?.('');
        }
    }, [options, localValue, onChange]);

    const classes = classNames(className, {
        [styles.Root]: true,
        [fieldStyles.Field]: true,
        [fieldStyles['has-errors']]: filteredErrors && filteredErrors.length,
        [styles.hasErrors]: filteredErrors && filteredErrors.length,
        [styles.inline]: inline,
        [styles.preventShrink]: preventShrink,
    });

    const inputClasses = classNames(
        fieldStyles.Input,
        fieldStyles.VisualInput,
        styles.Input,
        {
            [styles.Input_variantDefault]: variant === 'default',
            [styles.Input_variantDark]: variant === 'dark',
            [styles.Input_variantButton]: variant === 'button',
            [styles.Input_variantWhiteNoBorder]: variant === 'white-no-border',
            [styles.Input_variantSubtleNoBorder]:
                variant === 'subtle-no-border',
            [styles.Input_nonEmpty]: !!localValue,
            [styles.Input_sizeSmall]: size === 'small',
            [styles.Input_sizeMedium]: size === 'medium',
            [styles.Input_sizeLarge]: size === 'large',
            [styles.Input_readOnly]: readOnly || disabled,
            [styles.Input_bold]: bold,
        }
    );

    const content = (
        <span className={fieldStyles.InputWrapper}>
            <span className={styles.SelectAndIconWrapper}>
                <select
                    ref={selectRef}
                    className={inputClasses}
                    disabled={disabled}
                    defaultValue={defaultValue}
                    onChange={handleChange}
                    {...inputProps}
                >
                    {placeholder && <option value="">{placeholder}</option>}

                    {renderOptions(options)}
                </select>
                <SVG
                    className={classNames(styles.Icon, {
                        [styles.Icon_compact]: size === 'small',
                        [styles.Icon_darkMode]: variant === 'dark',
                        [styles.Icon_light]: variant === 'button',
                    })}
                    src={icon}
                />
            </span>
            {children}
        </span>
    );

    const style: CSSProperties = {
        flex: flex === true ? 1 : flex,
    };

    if (label) {
        return (
            <label className={classes} style={style}>
                <Stack space="xsmall">
                    <Label>{label}</Label>
                    {content}
                    <ErrorMessage className={fieldStyles.Errors}>
                        {filteredErrors}
                    </ErrorMessage>
                </Stack>
            </label>
        );
    }

    return (
        <label className={classes} style={style}>
            <Stack space="small">
                {content}
                <ErrorMessage className={fieldStyles.Errors}>
                    {filteredErrors}
                </ErrorMessage>
            </Stack>
        </label>
    );
}

export default Select;
